/**
 * The sentence for a refused field, in the visitor's language (1.31.0,
 * shared-chatbot#577): from the form's own check (details.ts / lead.ts), or
 * from chat_lead's `{code, field}` when the server refused what the form let
 * through. Pure, so scripts/test-config.mjs pins the words.
 *
 * Since 1.34.0 (shared-audit#17, field-rules migration pair 2) every problem
 * is the shared field library's `{code, params}`, and the widget's words for
 * each code are a library `FieldMessageBundle` — one per language, built from
 * that language's strings (strings.ts: en, it, fr, de, es), so the words a
 * visitor reads did not change with the rules. The library's own en-GB and
 * Italian sentences are not the widget's and are not shipped.
 *
 * A server refusal is read in EITHER vocabulary: the underscore codes
 * chat_lead sends today (`phone_too_long`, `name_invalid_characters` with
 * `rule: web_address`) and the library's dotted ones (`phone.too_long`,
 * `name.web_address`) that a later wave puts on the wire.
 *
 * The server's English `error` is never shown and never parsed: until 1.31.0
 * the form searched it for "email" and "name" to guess which box it was about.
 */
import type { ContactDetail } from './details';
import { EMAIL_MAX, PERSON_NAME_MAX, PHONE_KIND, countryName } from './fields';
import type { LeadExtraField, LeadRefusal } from './lead';
import { fill, type WidgetStrings } from './strings';
import {
  FIELD_CODES,
  phoneRule,
  type FieldCode,
  type FieldMessageBundle,
  type FieldMessageContext,
  type FieldParams,
  type FieldProblem,
} from './vendored/fields';

type Reason = 'required' | 'invalid' | 'tooLong';

/** "10", or "7–10": how many digits follow the dial code. */
export function digitsText(min: number, max: number): string {
  return min === max ? `${max}` : `${min}–${max}`;
}

const BUNDLES = new WeakMap<WidgetStrings, FieldMessageBundle>();

/**
 * The widget's words for every code the shared rules can answer, in `ui`'s
 * language: the library's `FieldMessageBundle` type, the widget's sentences.
 * A code none of the widget's forms can meet (a business name, a web
 * address, a landline where a mobile is needed) says the form's general
 * "check your details" — the bundle is complete by type, so a code the
 * library adds later fails the build here rather than drawing nothing.
 */
export function fieldBundle(ui: WidgetStrings): FieldMessageBundle {
  const cached = BUNDLES.get(ui);
  if (cached) return cached;
  const label = (c: FieldMessageContext) => c.label ?? '';
  const country = (p: FieldParams, c: FieldMessageContext) => (p.country ? countryName(p.country, c.locale) : '');
  const phone = (template: string) => (p: FieldParams, c: FieldMessageContext) =>
    fill(template, { country: country(p, c), dial: p.dial ?? '', digits: digitsText(p.min ?? 0, p.max ?? 0) });
  const general = () => ui.leadInvalid;
  const bundle: FieldMessageBundle = {
    // The form picks first or last by the box; a label says which.
    'name.required': (_, c) => (c.label === ui.detailsLastName ? ui.leadLastNameRequired : ui.leadFirstNameRequired),
    'name.too_long': (p, c) => fill(ui.nameTooLong, { label: label(c), max: String(p.max ?? PERSON_NAME_MAX) }),
    'name.invalid_characters': (_, c) => fill(ui.nameCharacters, { label: label(c) }),
    'name.needs_letter': (_, c) => fill(ui.nameNeedsLetter, { label: label(c) }),
    'name.web_address': (_, c) => fill(ui.nameWebAddress, { label: label(c) }),
    'business_name.required': general,
    'business_name.too_short': general,
    'business_name.too_long': general,
    'business_name.invalid_characters': general,
    'email.required': () => ui.emailRequired,
    'email.too_long': (p) => fill(ui.emailTooLong, { max: String(p.max ?? EMAIL_MAX) }),
    'email.spaces': () => ui.emailSpaces,
    'email.no_at': () => ui.emailNoAt,
    // The words a second @ has always had here: an address needs ONE @.
    'email.several_at': () => ui.emailNoAt,
    'email.invalid_local_part': () => ui.leadEmailInvalid,
    'email.invalid_domain': () => ui.emailDomain,
    'email.invalid': () => ui.leadEmailInvalid,
    'phone.required': () => ui.bookingPhoneRequired,
    'phone.invalid_characters': () => ui.leadPhoneInvalid,
    'phone.needs_country_code': () => ui.leadPhoneInvalid,
    'phone.unknown_country_code': (p) => (p.otherDial ? fill(ui.phoneUnknownCode, { otherDial: p.otherDial }) : ui.leadPhoneInvalid),
    'phone.country_mismatch': (p, c) => {
      const other = p.otherCountry ? countryName(p.otherCountry, c.locale) : p.otherDial ?? '';
      return fill(ui.phoneWrongCountry, {
        country: country(p, c),
        dial: p.dial ?? '',
        digits: digitsText(p.min ?? 0, p.max ?? 0),
        otherDial: p.otherDial ?? '',
        other,
      });
    },
    'phone.too_long': phone(ui.phoneTooLong),
    'phone.too_short': phone(ui.phoneTooShort),
    // A visitor's number is `any`: never held to a mobile range.
    'phone.not_mobile': () => ui.leadPhoneInvalid,
    // North America's area-code rule, or (shared-react-ui 0.19.1,
    // `startsWith`) a 00 after the code where no number starts 00:
    // "+47 00581130" says so, naming the country, its code and the 00.
    'phone.not_valid': (p, c) =>
      p.startsWith
        ? fill(ui.phoneStartsWith, { country: country(p, c), dial: p.dial ?? '', startsWith: p.startsWith })
        : phone(ui.phoneNotValid)(p, c),
    'url.required': general,
    'url.too_long': general,
    'url.invalid': general,
    'url.not_public': general,
    'url.not_https': general,
    'text.required': (_, c) => fill(ui.leadFieldRequired, { label: label(c) }),
    'text.too_long': (p, c) => fill(ui.textTooLong, { label: label(c), max: String(p.max ?? ''), count: String(p.count ?? '') }),
    'text.too_short': general,
    'text.invalid_characters': general,
  };
  BUNDLES.set(ui, bundle);
  return bundle;
}

/**
 * The sentence for a problem the shared rules found, in `ui`'s language —
 * the library's `fieldMessage(problem, {bundle})`, called on the bundle
 * directly so the library's own en-GB and Italian bundles (which
 * `fieldMessage` falls back to) are left out of the customer-site bundle.
 */
export function problemSentence(ui: WidgetStrings, problem: FieldProblem, language: string, label?: string): string {
  return fieldBundle(ui)[problem.code](problem.params, { label, locale: language });
}

/** The sentence for a phone the rules refused, naming its country. */
export function phoneSentence(ui: WidgetStrings, problem: FieldProblem, language: string): string {
  return problemSentence(ui, problem, language);
}

export interface FieldContext {
  ui: WidgetStrings;
  language: string;
  /** The label drawn over a box, by the form's field name. */
  label: (field: string) => string;
  /** The business's own questions, to tell a dropdown from a text box. */
  extraFields?: readonly LeadExtraField[];
  /** What the box holds, for "this is {count}". */
  length?: (field: string) => number;
  /** A box's limit, for "{max}". */
  limit?: (field: string) => number;
  /** The country the phone box was set to: a refusal that names none is about it. */
  phoneCountry?: string;
}

const CODES: ReadonlySet<string> = new Set(FIELD_CODES);

const isProblem = (detail: ContactDetail | undefined): detail is FieldProblem =>
  !!detail && typeof detail === 'object' && CODES.has(detail.code);

/** The sentence for the form's own refusal of `field`. */
export function fieldSentence(ctx: FieldContext, field: string, reason: Reason, detail?: ContactDetail): string {
  const { ui, language } = ctx;
  const label = ctx.label(field);
  if (field === 'phone') {
    if (reason === 'required') return ui.bookingPhoneRequired;
    return isProblem(detail) ? problemSentence(ui, detail, language, label) : ui.leadPhoneInvalid;
  }
  if (field === 'firstName' || field === 'lastName') {
    if (reason === 'required') return field === 'firstName' ? ui.leadFirstNameRequired : ui.leadLastNameRequired;
    return isProblem(detail) ? problemSentence(ui, detail, language, label) : fill(ui.nameCharacters, { label });
  }
  if (field === 'email') {
    if (reason === 'required') return ui.emailRequired;
    return isProblem(detail) ? problemSentence(ui, detail, language, label) : ui.leadEmailInvalid;
  }
  if (reason === 'tooLong') {
    const max = ctx.limit?.(field);
    const count = ctx.length?.(field);
    return max && count ? fill(ui.textTooLong, { label, max: String(max), count: String(count) }) : ui.leadTooLong;
  }
  if (field === 'message') return ui.leadMessageRequired;
  const extra = ctx.extraFields?.find((f) => f.id === field);
  if (extra?.type === 'select') return fill(ui.selectRequired, { label });
  return fill(ui.leadFieldRequired, { label });
}

/**
 * chat_lead's underscore codes, as the shared library names the same rule.
 * `phone_invalid` covers the library's invalid characters, unknown code and
 * North American digits alike, and says the one sentence it always has.
 */
const FROM_WIRE: Readonly<Record<string, FieldCode>> = {
  name_required: 'name.required',
  name_too_long: 'name.too_long',
  name_invalid_characters: 'name.invalid_characters',
  name_needs_letter: 'name.needs_letter',
  email_required: 'email.required',
  email_too_long: 'email.too_long',
  email_invalid: 'email.invalid',
  phone_required: 'phone.required',
  phone_invalid: 'phone.invalid_characters',
  phone_needs_country_code: 'phone.needs_country_code',
  phone_country_mismatch: 'phone.country_mismatch',
  phone_too_long: 'phone.too_long',
  phone_too_short: 'phone.too_short',
  phone_not_mobile: 'phone.not_mobile',
};

/** '44' or '+44' → '+44'; '' → undefined. */
const plus = (dial: string | undefined) => (dial ? `+${dial.replace(/^\+/, '')}` : undefined);

/**
 * A server refusal as the shared library's problem, whichever vocabulary it
 * came in, or null for a code that is not a field rule's (`required`,
 * `too_long` and `invalid_choice` are lead_form's own, for the message and the
 * business's questions). The facts a sentence needs come from the refusal —
 * `limit`, a phone's `country` and `dial`, the country it belongs to
 * (`numberCountry`/`numberDial`, or the library's `otherCountry`/`otherDial`)
 * — and a phone's lengths from the shared table for its country, which is the
 * form's own when the refusal names none.
 */
export function refusalProblem(refusal: LeadRefusal, phoneCountry = ''): FieldProblem | null {
  const sent = (refusal.code ?? '').trim().toLowerCase();
  let code: FieldCode | undefined = CODES.has(sent) ? (sent as FieldCode) : FROM_WIRE[sent];
  if (!code) return null;
  if (code === 'name.invalid_characters' && refusal.rule === 'web_address') code = 'name.web_address';
  const params: FieldParams = {};
  if (code.startsWith('phone.')) {
    const country = (refusal.country || phoneCountry || '').toUpperCase();
    const rule = country ? phoneRule(country, PHONE_KIND) : undefined;
    params.kind = PHONE_KIND;
    if (country) params.country = country;
    const dial = plus(refusal.dial) ?? rule?.dial;
    if (dial) params.dial = dial;
    const min = refusal.min ?? rule?.min;
    const max = refusal.max ?? rule?.max;
    if (min !== undefined) params.min = min;
    if (max !== undefined) params.max = max;
    const otherDial = plus(refusal.otherDial || refusal.numberDial);
    if (otherDial) params.otherDial = otherDial;
    const otherCountry = refusal.otherCountry || refusal.numberCountry;
    if (otherCountry) params.otherCountry = otherCountry;
    if (refusal.startsWith) params.startsWith = refusal.startsWith;
  }
  if (refusal.limit !== undefined) {
    if (code.endsWith('.too_long')) params.max = refusal.limit;
    if (code.endsWith('.too_short')) params.min = refusal.limit;
  }
  return { code, params };
}

/**
 * chat_lead's refusal as the form draws it: the box it names — `name` is the
 * first-name box, an unknown one is the form as a whole — and its sentence.
 * A required question this page never drew means the business changed its
 * form after the page loaded; the visitor can only reload.
 */
export function refusalSentence(
  ctx: FieldContext,
  refusal: LeadRefusal,
  drawn: readonly string[],
): { field: string; text: string } {
  const { ui, language } = ctx;
  const field = refusal.field === 'name' ? 'firstName' : refusal.field;
  const onForm = drawn.includes(field);
  const at = (text: string) => ({ field: onForm ? field : '', text });
  const label = ctx.label(field);
  const problem = refusalProblem(refusal, ctx.phoneCountry);
  if (problem) {
    // "Enter your first name" / "last name" by the box, as the form says it.
    if (problem.code === 'name.required') return at(field === 'lastName' ? ui.leadLastNameRequired : ui.leadFirstNameRequired);
    return at(problemSentence(ui, problem, language, label));
  }
  switch (refusal.code) {
    case 'required':
      if (!onForm) return { field: '', text: ui.leadFormChanged };
      return at(fieldSentence(ctx, field, 'required'));
    case 'too_long':
      return at(refusal.limit ? fill(ui.textTooLong, { label, max: String(refusal.limit), count: String(ctx.length?.(field) ?? refusal.limit + 1) }) : ui.leadTooLong);
    case 'invalid_choice':
      return at(fill(ui.selectRequired, { label }));
    default:
      return { field: '', text: ui.leadInvalid };
  }
}
